// Small DOM helpers. Props are applied after children so <select value> and <textarea value> work.
export function el(tag, props = {}, ...children) {
  const node = document.createElement(tag);
  node.append(...children.flat(Infinity).filter((c) => c !== null && c !== undefined && c !== false));
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === "class") node.className = value;
    else if (key === "dataset") Object.assign(node.dataset, value);
    else if (key.startsWith("on") && typeof value === "function") node.addEventListener(key.slice(2), value);
    else if (!key.includes("-") && key in node) node[key] = value;
    else node.setAttribute(key, value === true ? "" : value);
  }
  return node;
}

export const srOnly = (text) => el("span", { class: "sr-only" }, text);

// Short visible text; extra detail for screen readers. The visible text starts the accessible name.
export function actionButton(label, detail, props = {}) {
  return el("button", { type: "button", ...props }, label, detail ? srOnly(` ${detail}`) : null);
}

export function field({ id, label, hint, control }) {
  control.id = id;
  const hintId = hint ? `${id}-hint` : null;
  if (hintId) control.setAttribute("aria-describedby", hintId);
  return el("div", { class: "field" },
    el("label", { for: id }, label),
    control,
    hint ? el("p", { class: "hint", id: hintId }, hint) : null);
}

export function announce(message) {
  const live = document.getElementById("announcer");
  if (!live) return;
  live.textContent = "";
  setTimeout(() => (live.textContent = message), 60);
}

const busyContent = new WeakMap(); // a button's own children (incl. .sr-only text) while it shows a busy label

// Ice shimmer + aria-busy while something is working.
export function setBusy(element, busy, busyLabel) {
  element.classList.toggle("ice-busy", busy);
  element.setAttribute("aria-busy", String(busy));
  if (element.tagName !== "BUTTON") return;
  if (busy) {
    if (busyLabel) {
      busyContent.set(element, [...element.childNodes]);
      element.textContent = busyLabel;
    }
    element.disabled = true;
  } else {
    const children = busyContent.get(element);
    if (children) element.replaceChildren(...children);
    busyContent.delete(element);
    element.disabled = false;
  }
}

export function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

export function downloadBlob(content, filename, type = "application/octet-stream") {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = el("a", { href: url, download: filename });
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
